// @ts-nocheck
import { useCallback, useRef } from 'react';
import axios from 'axios';

import SwalToast from '../../components/Common/swalToast';
import API_ENDPOINTS from '../../const/ApiEndPoints';

import { initializeAxios } from '../../utils/Axios';
import {
  COMMON_AUTH_CONFIG,
  isCommonAuthEnabled,
  validateCommonAuthParams,
} from '../../const/commonAuthConfig';
import {
  redirectToLogout,
  clearCommonAuthData,
} from '../../utils/commonAuthUtils';
import useUser from './useUser';
import { extractAuthParams } from '../../utils/Utils';
import { notifySessionLogout } from '../../utils/sessionBroadcast';

/**
 * Custom hook for handling Common Auth System
 * This hook manages authentication flow for the unified auth service
 */
const useCommonAuth = () => {
  const { getUserRelatedDetails } = useUser();

  // Add a ref to track if initialization is in progress and store the promise
  const initializationInProgress = useRef(false);
  const initializationPromise = useRef(null);

  /**
   * Common Auth specific error handler
   * Clears all authentication data and redirects to google.com
   */
  const handleCommonAuthError = useCallback(
    (_error) => {
      // Clear all authentication data using centralized utility
      clearCommonAuthData();

      // Show error message

      // Redirect to logout
      setTimeout(() => {
        redirectToLogout();
      }, 1000);
    },
    [clearCommonAuthData, redirectToLogout]
  );

  // This function can be used in the future if we decide to make the common auth tenant wise, because then we will call this function first and get all the org level data like theme and tenant id and common auth is enabled in the tenant or not and using that we set the global key in local storage which we will use everywhere to validate if common auth is on or not like COMMON_AUTH_LOGIN variable in env. We will then remove the COMMON_AUTH_LOGIN variable as it is used for env level
  /**
   * Common Auth specific validateAccountId function - FUTURE IMPLEMENTATION
   * Validates the account/realm ID and stores theme and configuration data
   * @param {string} realmId - The realm/account ID to validate
   * @returns {Promise<boolean>} Success status
   *
   * NOSONAR - This entire block is future implementation code
   */
  // const validateCommonAuthAccountId = useCallback(
  //   async (realmId) => {
  //     try {
  //       // Add timeout to prevent hanging
  //       const timeoutPromise = new Promise((_, reject) => {
  //         setTimeout(
  //           () => reject(new Error('Account validation timeout')),
  //           10000
  //         );
  //       });
  // NOSONAR
  //       const validatePromise = validateAccount({ account_id: realmId });

  //         return true;
  //       } else {
  //         handleCommonAuthError(
  //           new Error('Invalid account validation response')
  //         );
  //         return false;
  //       }
  //     } catch (error) {
  //       // Log the error for debugging purposes
  //       console.error('Account validation failed:', error?.message);

  //       return false; // Return false to stop authentication
  //     }
  //   },
  //   [
  //     validateAccount,
  //     setCurrentSavedTheme,
  //     commonDispatch,
  //     setTriggerThemeChange,
  //     handleCommonAuthError,
  //   ]
  // );

  /**
   * Extracts tokens and parameters from URL
   * @returns {Object|null} Extracted parameters or null if invalid
   */
  const extractTokensFromUrl = useCallback(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);

      const extractedParams = extractAuthParams(urlParams);
      // Validate required parameters using centralized validation function
      if (!validateCommonAuthParams(extractedParams)) {
        return null;
      }

      // Extract all common auth parameters using utility function

      return {
        accessToken: extractedParams?.access_token,
        refreshToken: extractedParams?.refresh_token,
        realmId: extractedParams?.realm_id,
        sessionId: extractedParams?.session_id,
        // deviceId: params?.device_id,
      };
    } catch (__error) {
      return null;
    }
  }, []);

  /**
   * Stores authentication data in localStorage
   * @param {Object} authData - Authentication data
   */
  const storeAuthData = useCallback((authData) => {
    // Store tokens using same keys as existing auth system
    localStorage.setItem('access_token', authData?.accessToken);
    localStorage.setItem('refresh_token', authData?.refreshToken);
    localStorage.setItem('REALM_ID', authData?.realmId);

    // Store additional common auth specific data for future use
    if (authData?.sessionId) {
      localStorage.setItem('session_id', authData?.sessionId);
    }
  }, []);

  /**
   * Handles the complete authentication flow
   * @param {Object} authData - Extracted authentication data
   */
  const processAuthentication = useCallback(
    async (authData) => {
      try {
        // NOSONAR - Future implementation: Step 1: Validate account ID first (as per requirements)
        // const isValidAccount = await validateCommonAuthAccountId(
        //   authData?.realmId
        // );
        // NOSONAR
        // if (!isValidAccount) {
        //   // validateCommonAuthAccountId already handles error and redirect
        //   return false;
        // }

        // Step 1: Store authentication tokens after successful validation
        storeAuthData(authData);
        // Step 6: Call user data api and Dispatch login success action inside it
        await getUserRelatedDetails(authData?.accessToken, authData?.realmId);

        // Step 7: Initialize axios interceptors for common auth
        try {
          // Step 5.5: Small delay to ensure all localStorage operations complete
          // before any potential API calls that need the auth data
          await new Promise((resolve) => setTimeout(resolve, 1));
          initializeAxios();
        } catch (_axiosError) {
          // Don't fail the authentication for axios initialization issues
        }

        return true;
      } catch (error) {
        handleCommonAuthError(error);
        return false;
      }
    },
    [storeAuthData, handleCommonAuthError, getUserRelatedDetails]
  );

  /**
   * Cleans URL parameters after authentication
   */
  const cleanUrlParameters = useCallback(() => {
    try {
      const url = new URL(window.location);
      const paramsToRemove = [
        'access_token',
        'refresh_token',
        'realm_id',
        'session_id',
        'device_id',
        'auth',
      ];

      paramsToRemove.forEach((param) => {
        url.searchParams.delete(param);
      });

      // Replace current URL without the sensitive parameters
      window.history.replaceState({}, document.title, url.toString());
    } catch (_error) {
      // Intentionally ignore URL manipulation errors as they don't affect functionality
    }
  }, []);

  /**
   * Handles authentication error by redirecting to google.com
   */
  const handleAuthError = useCallback(() => {
    // Redirect to logout
    setTimeout(() => {
      redirectToLogout();
    }, 2000);
  }, []);

  /**
   * Initializes common auth flow if URL contains auth parameters
   */
  const initializeCommonAuth = useCallback(async () => {
    // If initialization is already in progress, wait for the existing promise
    if (initializationInProgress.current && initializationPromise.current) {
      return await initializationPromise.current;
    }

    // If there's no active promise, start the initialization
    if (!initializationInProgress.current) {
      initializationInProgress.current = true;

      // Create a new promise for this initialization
      initializationPromise.current = (async () => {
        try {
          if (!isCommonAuthEnabled()) {
            return false;
          }

          const authData = extractTokensFromUrl();

          if (authData) {
            const success = await processAuthentication(authData);

            if (success) {
              // Clean URL parameters after successful authentication
              cleanUrlParameters();
              return true;
            } else {
              handleAuthError();
              return false;
            }
          }

          return false;
        } finally {
          // Reset the flags after initialization completes
          initializationInProgress.current = false;
          initializationPromise.current = null;
        }
      })();

      return await initializationPromise.current;
    }

    return false;
  }, [
    extractTokensFromUrl,
    processAuthentication,
    cleanUrlParameters,
    handleAuthError,
  ]);

  /**
   * Logs out user from common auth
   */
  const logoutCommonAuth = useCallback(async () => {
    try {
      // Prepare logout request body
      const body = {
        session_id: localStorage.getItem('session_id'),
      };

      // Call the common auth logout API
      await axios
        .post(
          `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.LOGOUT_USER_COMMON_AUTH}`,
          body,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          }
        )
        .then((res) => {
          return res;
        });

      // Clear all auth-related data using centralized utility
      clearCommonAuthData();
      notifySessionLogout();

      SwalToast({
        icon: 'success',
        title: 'Successfully logged out',
      });

      // Redirect directly to logout URL
      redirectToLogout(COMMON_AUTH_CONFIG.LOGOUT_REDIRECT_DELAY, false);
    } catch (_error) {
      // Even if API call fails, ensure we clear local data and redirect
      clearCommonAuthData();
      notifySessionLogout();

      SwalToast({
        icon: 'error',
        title: 'Error during logout',
      });

      // Still redirect to logout even on error
      redirectToLogout(COMMON_AUTH_CONFIG.LOGOUT_REDIRECT_DELAY, false);
    }
  }, [clearCommonAuthData, redirectToLogout]);

  return {
    isCommonAuthEnabled,
    initializeCommonAuth,
    logoutCommonAuth,
    extractTokensFromUrl,
    cleanUrlParameters,
    handleCommonAuthError,
  };
};

export default useCommonAuth;
